import React, { useState, useEffect } from "react";
import {
  Box,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  FormControlLabel,
  Checkbox,
  Autocomplete,
  CircularProgress,
  RadioGroup,
  Radio,
  Snackbar,
  Alert,
} from "@mui/material";
import {
  Add as AddIcon,
  Search as SearchIcon,
  Close as CloseIcon,
} from "@mui/icons-material";
import { BASE_URL } from "../../../services/configUrls";
import CreateTextStatusModalForNew from "./CreateTextStatusModalForMem";

const AppleStyleSpinner = () => (
  <CircularProgress size={16} thickness={6} sx={{ color: "inherit" }} />
);

const AddFeatureToInstituteModal = ({
  open,
  onClose,
  instituteId,
  existingFeatures = [],
  onFeatureAdded,
}) => {
  const [availableFeatures, setAvailableFeatures] = useState([]);
  const [statusOptions, setStatusOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [featureSearchQuery, setFeatureSearchQuery] = useState("");
  const [selectedFeatures, setSelectedFeatures] = useState([]);
  const [featureTypes, setFeatureTypes] = useState({});
  const [featureStatusValues, setFeatureStatusValues] = useState({});

  // New feature creation states
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [newFeatureNameInput, setNewFeatureNameInput] = useState("");
  const [newFeatureTypeInput, setNewFeatureTypeInput] = useState("text");
  const [creatingFeature, setCreatingFeature] = useState(false);

  // Create Text Status Modal states
  const [openCreateStatusModal, setOpenCreateStatusModal] = useState(false);
  const [newStatusText, setNewStatusText] = useState("");
  const [newStatusSymbol, setNewStatusSymbol] = useState("");
  const [currentEditingFeature, setCurrentEditingFeature] = useState(null);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  useEffect(() => {
    if (open) {
      fetchFeatures();
      fetchStatusOptions();
    }
  }, [open]);

  const fetchFeatures = async () => {
    setLoading(true);
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${BASE_URL}/accounts/all-feature`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (result && Array.isArray(result)) {
        setAvailableFeatures(result);
      }
    } catch (error) {
      console.error("Error fetching features:", error);
    } finally {
      setLoading(false);
    }
  };

  const fetchStatusOptions = async () => {
    try {
      const accessToken = localStorage.getItem("accessToken");
      const response = await fetch(`${BASE_URL}/accounts/all-status-options`, {
        method: "GET",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
      });

      const result = await response.json();

      if (result && Array.isArray(result)) {
        const transformedStatuses = result.map((status) => ({
          id: status.id,
          text: status.status_text,
          symbol: status.symbol,
          color: getColorForSymbol(status.symbol),
        }));
        setStatusOptions(transformedStatuses);
      }
    } catch (error) {
      console.error("Error fetching status options:", error);
    }
  };

  const getColorForSymbol = (symbol) => {
    switch (symbol) {
      case "✓":
        return { bg: "#D1FAE5", color: "#065F46" };
      case "✗":
        return { bg: "#F3F4F6", color: "#374151" };
      case "*":
        return { bg: "#DBEAFE", color: "#1E40AF" };
      default:
        return { bg: "#E0E7FF", color: "#3730A3" };
    }
  };

  const handleClose = () => {
    if (!submitting && !creatingFeature) {
      setFeatureSearchQuery("");
      setSelectedFeatures([]);
      setFeatureTypes({});
      setFeatureStatusValues({});
      setShowCreateForm(false);
      setNewFeatureNameInput("");
      setNewFeatureTypeInput("text");
      onClose();
    }
  };

  const handleFeatureToggle = (featureName) => {
    setSelectedFeatures((prev) =>
      prev.includes(featureName)
        ? prev.filter((f) => f !== featureName)
        : [...prev, featureName]
    );
  };

  const handleFeatureTypeChange = (featureName, type) => {
    setFeatureTypes((prev) => ({ ...prev, [featureName]: type }));
  };

  const handleFeatureStatusValueChange = (featureName, value) => {
    setFeatureStatusValues((prev) => ({ ...prev, [featureName]: value }));
  };

  const handleAddFeatureBulk = async () => {
    if (selectedFeatures.length === 0 || !instituteId) {
      return;
    }

    setSubmitting(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const features = selectedFeatures.map((featureName) => {
        const feature = availableFeatures.find(
          (f) => f.feature_name === featureName
        );

        if (feature.type === "Count") {
          return {
            feature_id: feature.feature_id,
            value_text: null,
            value_count: parseInt(featureStatusValues[featureName] || "0"),
          };
        } else {
          const statusValue = featureStatusValues[featureName] || "TBA";
          return {
            feature_id: feature.feature_id,
            value_text: statusValue,
            value_count: null,
          };
        }
      });

      const payload = {
        features: features,
      };

      const response = await fetch(
        `${BASE_URL}/accounts/institute/${instituteId}/add-features-bulk`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${accessToken}`,
          },
          body: JSON.stringify(payload),
        }
      );

      const result = await response.json();

      if (response.ok) {
        if (onFeatureAdded) {
          await onFeatureAdded();
        }
        setFeatureSearchQuery("");
        setSelectedFeatures([]);
        setFeatureTypes({});
        setFeatureStatusValues({});
        onClose();
      } else {
        alert(result.message || "Failed to add features");
      }
    } catch (error) {
      console.error("Error adding features:", error);
      alert("Error adding features. Please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  const handleOpenCreateForm = () => {
    setShowCreateForm(true);
  };

  const handleCancelCreateForm = () => {
    setShowCreateForm(false);
    setNewFeatureNameInput("");
    setNewFeatureTypeInput("text");
  };

  const handleCreateFeature = async () => {
    if (!newFeatureNameInput.trim()) {
      return;
    }

    setCreatingFeature(true);
    try {
      const accessToken = localStorage.getItem("accessToken");

      const featureType =
        newFeatureTypeInput.charAt(0).toUpperCase() +
        newFeatureTypeInput.slice(1);

      const payload = {
        feature_name: newFeatureNameInput.trim(),
        type: featureType,
      };

      const response = await fetch(`${BASE_URL}/accounts/create-feature`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${accessToken}`,
        },
        body: JSON.stringify(payload),
      });

      const result = await response.json();

      if (response.ok) {
        // Refresh features list
        await fetchFeatures();
        // Reset form
        setNewFeatureNameInput("");
        setNewFeatureTypeInput("text");
        setShowCreateForm(false);
      } else {
        alert(result.message || "Failed to create feature");
      }
    } catch (error) {
      console.error("Error creating feature:", error);
      alert("Error creating feature. Please try again.");
    } finally {
      setCreatingFeature(false);
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === "Enter" && newFeatureNameInput.trim() && !creatingFeature) {
      handleCreateFeature();
    }
  };

  // Create Status Modal Handlers
  const handleOpenCreateStatusModal = (featureName) => {
    setCurrentEditingFeature(featureName);
    setOpenCreateStatusModal(true);
  };

  const handleCloseCreateStatusModal = () => {
    setOpenCreateStatusModal(false);
    setCurrentEditingFeature(null);
  };

  const handleCreateTextStatus = () => {
    // This will be called after status is created
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  const getFilteredAndSortedFeatures = () => {
    // First, filter out features that already exist in the institute
    const availableToAdd = availableFeatures.filter(
      (feature) =>
        !existingFeatures.some((f) => f.feature_name === feature.feature_name)
    );

    // Then, filter by search query (only feature name)
    const searchFiltered = availableToAdd.filter((feature) =>
      feature.feature_name
        .toLowerCase()
        .includes(featureSearchQuery.toLowerCase().trim())
    );

    // Finally, sort: selected features first, then unselected
    const selected = [];
    const unselected = [];

    searchFiltered.forEach((feature) => {
      if (selectedFeatures.includes(feature.feature_name)) {
        selected.push(feature);
      } else {
        unselected.push(feature);
      }
    });

    return [...selected, ...unselected];
  };

  const sortedFeatures = getFilteredAndSortedFeatures();

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        maxWidth="sm"
        fullWidth
        disableEscapeKeyDown={submitting || creatingFeature}
        TransitionProps={{
          timeout: 400,
        }}
        PaperProps={{
          sx: {
            animation: open ? "slideUp 0.3s ease-out" : "none",
            "@keyframes slideUp": {
              from: {
                opacity: 0,
                transform: "translateY(20px)",
              },
              to: {
                opacity: 1,
                transform: "translateY(0)",
              },
            },
          },
        }}
      >
        <DialogTitle sx={{ fontSize: "15px", fontWeight: 600, pb: 2 }}>
          Select Features
        </DialogTitle>

        <DialogContent sx={{ pt: 1, pb: 2 }}>
          <TextField
            fullWidth
            size="small"
            placeholder="Search by feature name..."
            value={featureSearchQuery}
            onChange={(e) => setFeatureSearchQuery(e.target.value)}
            disabled={submitting || showCreateForm}
            InputProps={{
              startAdornment: (
                <SearchIcon sx={{ fontSize: 18, color: "#9CA3AF", mr: 1 }} />
              ),
            }}
            sx={{
              mb: 2,
              "& .MuiOutlinedInput-root": {
                fontSize: "13px",
              },
            }}
          />

          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mb: 1.5,
              px: 0.5,
            }}
          >
            <Typography
              variant="body2"
              sx={{ fontSize: "12px", color: "#6C757D", fontWeight: 500 }}
            >
              {selectedFeatures.length > 0
                ? `${selectedFeatures.length} selected`
                : "Select features to add"}
            </Typography>
          </Box>

          <Box
            sx={{
              maxHeight: "400px",
              overflowY: "auto",
              border: "1px solid #E5E7EB",
              borderRadius: "6px",
              backgroundColor: "#FAFBFC",
              opacity: submitting ? 0.6 : 1,
              pointerEvents: submitting ? "none" : "auto",
              "&::-webkit-scrollbar": {
                width: "6px",
              },
              "&::-webkit-scrollbar-track": {
                backgroundColor: "#F3F4F6",
                borderRadius: "3px",
              },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: "#D1D5DB",
                borderRadius: "3px",
                "&:hover": {
                  backgroundColor: "#9CA3AF",
                },
              },
            }}
          >
            {loading ? (
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  py: 4,
                }}
              >
                <CircularProgress size={24} />
              </Box>
            ) : (
              <Box sx={{ p: 1 }}>
                {/* Create New Feature Form/Button */}
                {!showCreateForm ? (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: "6px",
                      border: "2px dashed #2D7FF9",
                      backgroundColor: "#F0F7FF",
                      cursor: "pointer",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        backgroundColor: "#E6F2FF",
                        transform: "translateY(-2px)",
                      },
                    }}
                    onClick={handleOpenCreateForm}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1,
                        justifyContent: "center",
                      }}
                    >
                      <AddIcon sx={{ fontSize: 18, color: "#2D7FF9" }} />
                      <Typography
                        sx={{
                          fontSize: "13px",
                          color: "#2D7FF9",
                          fontWeight: 600,
                        }}
                      >
                        Add New Feature
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      mb: 1.5,
                      p: 2,
                      borderRadius: "6px",
                      border: "1.5px solid #2D7FF9",
                      backgroundColor: "#F0F7FF",
                    }}
                  >
                    <Box
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        mb: 2,
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: "#2D7FF9",
                        }}
                      >
                        Create New Feature
                      </Typography>
                      <CloseIcon
                        sx={{
                          fontSize: 18,
                          color: "#6B7280",
                          cursor: "pointer",
                          "&:hover": { color: "#374151" },
                        }}
                        onClick={handleCancelCreateForm}
                      />
                    </Box>

                    <TextField
                      autoFocus
                      fullWidth
                      size="small"
                      label="Feature Name"
                      value={newFeatureNameInput}
                      onChange={(e) => setNewFeatureNameInput(e.target.value)}
                      placeholder="e.g., Virtual Internship, Technical Workshop"
                      onKeyPress={handleKeyPress}
                      disabled={creatingFeature}
                      sx={{ mb: 2 }}
                    />

                    <Box sx={{ mb: 2 }}>
                      <Typography
                        variant="body2"
                        sx={{
                          fontSize: "12px",
                          fontWeight: 600,
                          color: "#374151",
                          mb: 1,
                        }}
                      >
                        Feature Type
                      </Typography>
                      <RadioGroup
                        row
                        value={newFeatureTypeInput}
                        onChange={(e) => setNewFeatureTypeInput(e.target.value)}
                        disabled={creatingFeature}
                        sx={{ gap: 2 }}
                      >
                        <FormControlLabel
                          value="text"
                          control={
                            <Radio
                              size="small"
                              disabled={creatingFeature}
                              sx={{
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                sx={{ fontSize: "12px", fontWeight: 500 }}
                              >
                                Text
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  color: "#9CA3AF",
                                  mt: 0.25,
                                }}
                              >
                                For status values
                              </Typography>
                            </Box>
                          }
                        />
                        <FormControlLabel
                          value="count"
                          control={
                            <Radio
                              size="small"
                              disabled={creatingFeature}
                              sx={{
                                "& .MuiSvgIcon-root": { fontSize: 18 },
                              }}
                            />
                          }
                          label={
                            <Box>
                              <Typography
                                sx={{ fontSize: "12px", fontWeight: 500 }}
                              >
                                Count
                              </Typography>
                              <Typography
                                sx={{
                                  fontSize: "10px",
                                  color: "#9CA3AF",
                                  mt: 0.25,
                                }}
                              >
                                For numeric values
                              </Typography>
                            </Box>
                          }
                        />
                      </RadioGroup>
                    </Box>

                    <Box sx={{ display: "flex", gap: 1 }}>
                      <Button
                        onClick={handleCancelCreateForm}
                        disabled={creatingFeature}
                        size="small"
                        sx={{ fontSize: "12px" }}
                      >
                        Cancel
                      </Button>
                      <Button
                        onClick={handleCreateFeature}
                        variant="contained"
                        disabled={
                          !newFeatureNameInput.trim() || creatingFeature
                        }
                        startIcon={
                          creatingFeature ? <AppleStyleSpinner /> : null
                        }
                        size="small"
                        sx={{ fontSize: "12px" }}
                      >
                        {creatingFeature ? "Creating..." : "Create"}
                      </Button>
                    </Box>
                  </Box>
                )}

                {/* Existing Features List */}
                {sortedFeatures.map((feature) => (
                  <Box
                    key={feature.feature_id}
                    sx={{
                      mb: 1.5,
                      p: 1.5,
                      borderRadius: "6px",
                      border: selectedFeatures.includes(feature.feature_name)
                        ? "1.5px solid #2D7FF9"
                        : "1px solid #E5E7EB",
                      backgroundColor: selectedFeatures.includes(
                        feature.feature_name
                      )
                        ? "#F0F7FF"
                        : "#FFFFFF",
                      "&:hover": {
                        backgroundColor: selectedFeatures.includes(
                          feature.feature_name
                        )
                          ? "#E6F2FF"
                          : "#F9FAFB",
                      },
                      transition: "all 0.2s ease",
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={selectedFeatures.includes(
                            feature.feature_name
                          )}
                          onChange={() =>
                            handleFeatureToggle(feature.feature_name)
                          }
                          size="small"
                          sx={{
                            color: "#9CA3AF",
                            p: 0.5,
                            "&.Mui-checked": {
                              color: "#2D7FF9",
                            },
                          }}
                        />
                      }
                      label={
                        <Typography
                          sx={{
                            fontSize: "13px",
                            color: "#212529",
                            fontWeight: 500,
                          }}
                        >
                          {feature.feature_name}
                        </Typography>
                      }
                      sx={{
                        mx: 0,
                        mb: selectedFeatures.includes(feature.feature_name)
                          ? 1
                          : 0,
                      }}
                    />

                    {selectedFeatures.includes(feature.feature_name) && (
                      <Box sx={{ ml: 4, mt: 1 }}>
                        {feature.type === "Count" ? (
                          <>
                            {!featureTypes[feature.feature_name] &&
                              handleFeatureTypeChange(
                                feature.feature_name,
                                "count"
                              )}
                            <TextField
                              fullWidth
                              size="small"
                              type="number"
                              label="Count Value"
                              value={
                                featureStatusValues[feature.feature_name] || 0
                              }
                              onChange={(e) =>
                                handleFeatureStatusValueChange(
                                  feature.feature_name,
                                  e.target.value
                                )
                              }
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontSize: "13px",
                                },
                              }}
                            />
                          </>
                        ) : (
                          <>
                            {!featureTypes[feature.feature_name] &&
                              handleFeatureTypeChange(
                                feature.feature_name,
                                "text"
                              )}
                            <Autocomplete
                              freeSolo
                              fullWidth
                              options={statusOptions.map((st) => st.text)}
                              value={
                                featureStatusValues[feature.feature_name] ||
                                "TBA"
                              }
                              onChange={(event, newValue) => {
                                if (newValue) {
                                  handleFeatureStatusValueChange(
                                    feature.feature_name,
                                    newValue
                                  );
                                }
                              }}
                              onInputChange={(event, newInputValue) => {
                                handleFeatureStatusValueChange(
                                  feature.feature_name,
                                  newInputValue
                                );
                              }}
                              ListboxProps={{
                                style: { maxHeight: "250px", padding: 0 },
                              }}
                              ListboxComponent={React.forwardRef(
                                (props, ref) => {
                                  const { children, ...other } = props;
                                  return (
                                    <ul
                                      {...other}
                                      ref={ref}
                                      style={{ padding: 0, margin: 0 }}
                                    >
                                      {/* Sticky "Create New" option */}
                                      <Box
                                        component="li"
                                        onClick={() =>
                                          handleOpenCreateStatusModal(
                                            feature.feature_name
                                          )
                                        }
                                        sx={{
                                          position: "sticky",
                                          top: 0,
                                          zIndex: 10,
                                          backgroundColor: "#FFFFFF",
                                          display: "flex",
                                          alignItems: "center",
                                          gap: 1,
                                          padding: "8px 14px",
                                          cursor: "pointer",
                                          borderBottom: "1px solid #E5E7EB",
                                          listStyle: "none",
                                          "&:hover": {
                                            backgroundColor: "#F0F9FF",
                                          },
                                        }}
                                      >
                                        <AddIcon
                                          sx={{
                                            fontSize: 16,
                                            color: "#2D7FF9",
                                          }}
                                        />
                                        <Typography
                                          sx={{
                                            fontSize: "13px",
                                            color: "#2D7FF9",
                                            fontWeight: 600,
                                          }}
                                        >
                                          Create New...
                                        </Typography>
                                      </Box>
                                      {/* Scrollable status list */}
                                      {children}
                                    </ul>
                                  );
                                }
                              )}
                              renderOption={(props, option) => (
                                <li {...props} key={option}>
                                  <Box
                                    sx={{
                                      display: "flex",
                                      alignItems: "center",
                                      gap: 1,
                                    }}
                                  >
                                    <Box
                                      sx={{
                                        px: 1,
                                        py: 0.5,
                                        borderRadius: "3px",
                                        backgroundColor:
                                          statusOptions.find(
                                            (st) => st.text === option
                                          )?.color.bg || "#E0E7FF",
                                        color:
                                          statusOptions.find(
                                            (st) => st.text === option
                                          )?.color.color || "#3730A3",
                                        fontSize: "11px",
                                        fontWeight: 600,
                                      }}
                                    >
                                      {statusOptions.find(
                                        (st) => st.text === option
                                      )?.symbol || ""}
                                    </Box>
                                    <Typography sx={{ fontSize: "13px" }}>
                                      {option}
                                    </Typography>
                                  </Box>
                                </li>
                              )}
                              renderInput={(params) => (
                                <TextField
                                  {...params}
                                  label="Status"
                                  size="small"
                                  sx={{
                                    "& .MuiOutlinedInput-root": {
                                      fontSize: "13px",
                                    },
                                  }}
                                />
                              )}
                            />
                          </>
                        )}
                      </Box>
                    )}
                  </Box>
                ))}

                {!loading &&
                  sortedFeatures.length === 0 &&
                  !featureSearchQuery && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "13px",
                        color: "#9CA3AF",
                        textAlign: "center",
                        py: 4,
                      }}
                    >
                      All features have been added
                    </Typography>
                  )}

                {!loading &&
                  sortedFeatures.length === 0 &&
                  featureSearchQuery && (
                    <Typography
                      variant="body2"
                      sx={{
                        fontSize: "13px",
                        color: "#9CA3AF",
                        textAlign: "center",
                        py: 4,
                      }}
                    >
                      No features found matching "{featureSearchQuery}"
                    </Typography>
                  )}
              </Box>
            )}
          </Box>
        </DialogContent>

        <DialogActions sx={{ px: 3, pb: 2, pt: 1 }}>
          <Button
            onClick={handleClose}
            size="small"
            disabled={submitting || creatingFeature}
            sx={{ fontSize: "13px" }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleAddFeatureBulk}
            variant="contained"
            disabled={
              selectedFeatures.length === 0 || submitting || creatingFeature
            }
            startIcon={
              submitting ? (
                <AppleStyleSpinner />
              ) : (
                <AddIcon sx={{ fontSize: 16 }} />
              )
            }
            size="small"
            sx={{ fontSize: "13px" }}
          >
            {submitting
              ? "Adding..."
              : `Add ${
                  selectedFeatures.length > 0
                    ? `(${selectedFeatures.length})`
                    : ""
                }`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Create Text Status Modal */}
      <CreateTextStatusModalForNew
        open={openCreateStatusModal}
        onClose={handleCloseCreateStatusModal}
        newStatusText={newStatusText}
        setNewStatusText={setNewStatusText}
        newStatusSymbol={newStatusSymbol}
        setNewStatusSymbol={setNewStatusSymbol}
        onCreateTextStatus={handleCreateTextStatus}
        onRefreshStatusOptions={fetchStatusOptions}
        onShowSnackbar={setSnackbar}
        onResetTempValue={(val) => {
          if (val && currentEditingFeature) {
            handleFeatureStatusValueChange(currentEditingFeature, val);
          }
        }}
      />

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          variant="filled"
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
};

export default AddFeatureToInstituteModal;
